import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { SESSION_ENDED_CODES, SESSION_ENDED_CODES_WITH_OWN_MESSAGE } from "../utils/api";

const AuthContext = createContext();

// API Base URL for authentication
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.pkf-singapore.com";
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authState, setAuthState] = useState('logged_out'); // logged_out, otp_required, authenticated
  // True after a live session was ended by the server; Login shows why.
  const [sessionEnded, setSessionEnded] = useState(false);
  // Set only for the codes that carry their own explanation (EMAIL_CHANGED
  // names the new sign-in address) — Login prints this instead of the generic
  // "your session has ended" line.
  const [sessionEndedMessage, setSessionEndedMessage] = useState(null);
  const authStateRef = useRef(authState);
  authStateRef.current = authState;

  // Every API response passes through here: when a signed-in user gets one of
  // the auth middleware's session-ended codes, drop to the login screen. The
  // URL is untouched, so logging in again returns to the same page. Wrapping
  // fetch (instead of only fetchJSON) also covers pages that call fetch
  // directly.
  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if ((response.status === 401 || response.status === 403) && authStateRef.current === 'authenticated') {
        const body = await response.clone().json().catch(() => null);
        if (body && SESSION_ENDED_CODES.has(body.code)) {
          setUser(null);
          setAuthState('logged_out');
          setSessionEnded(true);
          setSessionEndedMessage(
            SESSION_ENDED_CODES_WITH_OWN_MESSAGE.has(body.code) ? body.error || null : null
          );
          localStorage.removeItem("pkf_dashboard_user");
        }
      }
      return response;
    };
    return () => { window.fetch = originalFetch; };
  }, []);

  useEffect(() => {
    if (authState === 'authenticated') { setSessionEnded(false); setSessionEndedMessage(null); }
  }, [authState]);

  // Check for existing session on app load. /api/auth/test returns the LIVE
  // permission list (the server reads permissions from the DB per request),
  // so a permission change is picked up on the next app load/refresh.
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // Test if we have a valid HTTPOnly cookie by making an authenticated request
        const response = await fetch(`${API_BASE_URL}/api/auth/test`, {
          method: "GET",
          credentials: "include", // Include HTTPOnly cookies
        });

        if (response.ok) {
          const data = await response.json();
          
          // Preserve existing user data if available, otherwise use server data
          const existingUser = localStorage.getItem("pkf_dashboard_user");
          let userData = {
            email: data.email || "Administrator",
            role: data.role,
            name: data.name || "Administrator",
            modules: data.modules || [],
            permissions: data.permissions || [],
            mustChangePassword: !!data.mustChangePassword,
            verified: true
          };
          
          // Preserve the display name from a previous session when the
          // server response does not carry one.
          if (existingUser) {
            try {
              const parsed = JSON.parse(existingUser);
              userData = {
                ...userData,
                email: data.email || parsed.email || "Administrator",
                name: data.name || parsed.name || userData.email?.split('@')[0] || "Administrator",
              };
            } catch (error) {
              console.warn("Error parsing existing user data:", error);
            }
          }

          setUser(userData);
          localStorage.setItem("pkf_dashboard_user", JSON.stringify(userData));
          setAuthState('authenticated');
          console.log("🔐 Authenticated via HTTPOnly cookie:", data.role);
        } else {
          // No valid authentication, clear any stored user data
          setUser(null);
          setAuthState('logged_out');
          localStorage.removeItem("pkf_dashboard_user");
        }
      } catch (error) {
        console.error("Error checking auth status:", error);
        setUser(null);
        setAuthState('logged_out');
        localStorage.removeItem("pkf_dashboard_user");
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const loginWithCredentials = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies in request
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }

      if (data.stage === 'otp_required') {
        setAuthState('otp_required');
        return { 
          success: true, 
          stage: 'otp_required', 
          maskedEmail: data.email,
          expiresIn: data.expiresIn 
        };
      }

      // Handle direct login success (returning user - no OTP required)
      if (data.stage === 'authenticated') {
        setUser(data.user);
        setAuthState('authenticated');
        localStorage.setItem("pkf_dashboard_user", JSON.stringify(data.user));
        console.log("🔐 Returning user - login successful via HTTPOnly cookie");
        return { success: true, stage: 'authenticated' };
      }

      // Fallback for any other success response
      setUser(data.user);
      setAuthState('authenticated');
      localStorage.setItem("pkf_dashboard_user", JSON.stringify(data.user));
      console.log("🔐 Login successful via HTTPOnly cookie");

      return { success: true };
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, error: error.message };
    }
  };

  const verifyOTP = async (otp) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ otp }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "OTP verification failed");
      }

      setUser(data.user);
      setAuthState('authenticated');
      localStorage.setItem("pkf_dashboard_user", JSON.stringify(data.user));
      console.log("🔐 OTP verification successful");
      
      return { success: true };
    } catch (error) {
      console.error("OTP verification error:", error);
      return { success: false, error: error.message };
    }
  };

  const resendOTP = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/resend-otp`, {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to resend OTP");
      }

      console.log("🔐 OTP resent successfully");
      return { success: true, message: data.message };
    } catch (error) {
      console.error("OTP resend error:", error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear HTTPOnly cookies
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include", // Include cookies in request
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Always clear local state
      setUser(null);
      setAuthState('logged_out');
      localStorage.removeItem("pkf_dashboard_user");
      console.log("🔐 Logged out - HTTPOnly cookies cleared");
    }
  };

  // Account administration: the seed super_admin (bypasses everything) or
  // anyone granted the admin:users permission.
  const isAdmin = () => {
    return user?.role === "super_admin" || (user?.permissions || []).includes("admin:users");
  };

  const isAuthenticated = () => {
    return authState === 'authenticated' && !!user;
  };

  const isOTPRequired = () => {
    return authState === 'otp_required';
  };

  const hasPermission = (action) => {
    if (!user || !isAuthenticated()) return false;

    // The seed super_admin rescue account holds every permission (mirrors the
    // server-side bypass); everyone else — including Admins — is checked
    // against their granted list.
    if (user.role === "super_admin") return true;

    // "module:action" strings are checked against the DB-driven permission
    // list (e.g. "qm:view", "engagements:edit"). Display-only — the real
    // enforcement is the backend middleware, which reads permissions live.
    return action.includes(":") && (user.permissions || []).includes(action);
  };

  // Force-password-change gate (users.must_change_password).
  const mustChangePassword = () => !!user?.mustChangePassword;

  const clearMustChangePassword = () => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, mustChangePassword: false };
      localStorage.setItem("pkf_dashboard_user", JSON.stringify(updated));
      return updated;
    });
  };

  const value = {
    user,
    authState,
    sessionEnded,
    sessionEndedMessage,
    loginWithCredentials,
    verifyOTP,
    resendOTP,
    logout,
    isAdmin,
    isAuthenticated,
    isOTPRequired,
    hasPermission,
    mustChangePassword,
    clearMustChangePassword,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
